import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { BarChart3, ChevronDown, Menu, X, Sparkles, Heart, LogOut, PlusSquare } from "lucide-react";
import { useAuth, login } from "@/context/AuthContext";
import { useSaved } from "@/context/SavedContext";

const NAV = [
  { to: "/homes/search?status=for_sale", label: "Buy" },
  { to: "/homes/list-property", label: "Sell" },
  { to: "/homes/search?status=for_rent", label: "Rent" },
  { to: "/homes/search?property_type=multifamily", label: "Commercial" },
  { to: "/homes/search?property_type=land", label: "Land" },
  { to: "/homes/search?status=for_sale", label: "Invest" },
  { to: "/homes", label: "Market Insights" },
];

export function HomesLogo({ className = "" }) {
  return (
    <Link to="/homes" className={`flex items-center gap-2.5 ${className}`} data-testid="homes-logo">
      <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#2563EB]">
        <BarChart3 className="h-5 w-5 text-white" strokeWidth={2.5} />
      </span>
      <span className="text-2xl font-black tracking-tight text-[#0F172A]">
        HomesPulse<span className="text-[#2563EB]">.pro</span>
      </span>
    </Link>
  );
}

export function HomesHeader() {
  const [open, setOpen] = useState(false);
  const { user, logout } = useAuth();
  const saved = useSaved();
  const alerts = saved?.alerts || { new_matches: 0, saved_listings: 0 };
  return (
    <header className="sticky top-0 z-40 border-b border-slate-200 bg-white" data-testid="homes-header">
      <div className="mx-auto flex max-w-[1400px] items-center gap-6 px-4 py-3.5">
        <HomesLogo />

        <nav className="ml-6 hidden items-center gap-1 lg:flex">
          {NAV.map((n, i) => (
            <Link key={i} to={n.to}
              className="rounded-lg px-3 py-2 text-[15px] font-semibold text-slate-700 transition-colors hover:text-[#2563EB]"
              data-testid={`homes-nav-${n.label.toLowerCase().replace(/\s/g, "-")}`}>
              {n.label}
            </Link>
          ))}
          <button className="flex items-center gap-1 rounded-lg px-3 py-2 text-[15px] font-semibold text-slate-700 transition-colors hover:text-[#2563EB]" data-testid="homes-nav-resources">
            Resources <ChevronDown className="h-4 w-4" />
          </button>
          <Link to="/homes/relocation" className="rounded-lg px-3 py-2 text-[15px] font-semibold text-slate-700 transition-colors hover:text-[#2563EB]" data-testid="homes-nav-relocation">
            Relocation
          </Link>
          <Link to="/homes/card-studio" className="rounded-lg px-3 py-2 text-[15px] font-semibold text-slate-700 transition-colors hover:text-[#2563EB]" data-testid="homes-nav-card-studio">
            Card Studio
          </Link>
          <Link to="/homes/subscribe" className="rounded-lg px-3 py-2 text-[15px] font-semibold text-slate-700 transition-colors hover:text-[#2563EB]" data-testid="homes-nav-agents-join">
            For Agents
          </Link>
          <Link to="/homes/advisor" className="ml-1 flex items-center gap-1.5 rounded-lg bg-[#EEF2FF] px-3 py-2 text-[15px] font-bold text-[#2563EB] transition-colors hover:bg-[#2563EB] hover:text-white" data-testid="homes-nav-advisor">
            <Sparkles className="h-4 w-4" /> Where to Live
          </Link>
          <Link to="/homes/list-property" className="ml-1 flex items-center gap-1.5 rounded-lg bg-[#2563EB] px-3.5 py-2 text-[15px] font-bold text-white transition-colors hover:bg-[#1D4ED8]" data-testid="homes-nav-list-property">
            <PlusSquare className="h-4 w-4" /> List a Property
          </Link>
        </nav>

        <div className="ml-auto hidden items-center gap-3 lg:flex">
          {user ? (
            <>
              <Link to="/homes/saved" className="relative flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm font-semibold text-slate-800 transition-colors hover:bg-slate-50" data-testid="homes-saved-link">
                <Heart className="h-4 w-4 text-[#2563EB]" /> Saved
                {alerts.new_matches > 0 && <span className="absolute -right-1 -top-1 flex h-5 min-w-5 items-center justify-center rounded-full bg-[#2563EB] px-1 text-[10px] font-bold text-white" data-testid="homes-alert-badge">{alerts.new_matches}</span>}
              </Link>
              {user.picture ? <img src={user.picture} alt="" className="h-8 w-8 rounded-full" /> : <span className="flex h-8 w-8 items-center justify-center rounded-full bg-[#0F172A] text-xs font-bold text-white">{(user.name || user.email || "U")[0].toUpperCase()}</span>}
              <button onClick={logout} className="rounded-lg border border-slate-300 px-3 py-2 text-sm font-semibold text-slate-800 transition-colors hover:bg-slate-50" data-testid="homes-signout"><LogOut className="h-4 w-4" /></button>
            </>
          ) : (
            <>
              <button onClick={() => login(window.location.pathname)} className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-800 transition-colors hover:bg-slate-50" data-testid="homes-signin">Sign In</button>
              <button onClick={() => login(window.location.pathname)} className="rounded-lg bg-[#2563EB] px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-[#1D4ED8]" data-testid="homes-signup">Sign Up</button>
            </>
          )}
        </div>

        <button className="ml-auto lg:hidden" onClick={() => setOpen((o) => !o)} data-testid="homes-menu-btn">
          {open ? <X className="h-6 w-6 text-[#0F172A]" /> : <Menu className="h-6 w-6 text-[#0F172A]" />}
        </button>
      </div>

      {open && (
        <div className="border-t border-slate-200 px-4 py-3 lg:hidden" data-testid="homes-mobile-nav">
          {NAV.map((n, i) => (
            <Link key={i} to={n.to} onClick={() => setOpen(false)} className="block rounded-lg px-3 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50">{n.label}</Link>
          ))}
          <div className="mt-2 flex gap-2">
            {user ? (
              <>
                <Link to="/homes/saved" onClick={() => setOpen(false)} className="flex-1 rounded-lg border border-slate-300 px-4 py-2 text-center text-sm font-semibold text-slate-800">Saved{alerts.new_matches > 0 ? ` (${alerts.new_matches})` : ""}</Link>
                <button onClick={() => { logout(); setOpen(false); }} className="flex-1 rounded-lg bg-slate-100 px-4 py-2 text-center text-sm font-semibold text-slate-800">Sign out</button>
              </>
            ) : (
              <>
                <button onClick={() => login(window.location.pathname)} className="flex-1 rounded-lg border border-slate-300 px-4 py-2 text-center text-sm font-semibold text-slate-800">Sign In</button>
                <button onClick={() => login(window.location.pathname)} className="flex-1 rounded-lg bg-[#2563EB] px-4 py-2 text-center text-sm font-semibold text-white">Sign Up</button>
              </>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
